import { ArrowLeft, Calendar, MapPin, Phone, Sparkles, Star, Store, Users } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { IRestaurantDatabase } from "../../interfaces/restaurantDatabase";
import restaurantData from "../../mock/restaurantDatabase.json";
import { Badge } from "../../components/ui/Badge";

export default function RestaurantDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const restaurant = (restaurantData as IRestaurantDatabase[]).find((r) => r.id === id);

    if (!restaurant) {
        return (
            <div className="min-h-screen bg-canvas font-sans flex items-center justify-center">
                <div className="text-center">
                    <Store className="w-16 h-16 text-ink-faint mx-auto mb-4" />
                    <h2 className="text-2xl font-semibold text-ink mb-2">Restaurant non trouvé</h2>
                    <p className="text-ink-muted mb-4">Ce restaurant n'existe pas ou a été supprimé.</p>
                    <button
                        onClick={() => navigate("/restaurants")}
                        className="px-4 py-2 bg-ink text-ink-inverse rounded-lg hover:bg-ink/90 transition-colors duration-150"
                    >
                        Retour à la liste
                    </button>
                </div>
            </div>
        );
    }

    const getOwnerTypeBadge = (ownerType: string) => {
        const badges: Record<string, { label: string; tone: "neutral" | "accent" | "success" }> = {
            'franchise': { label: 'Franchise', tone: 'neutral' },
            'multi-site': { label: 'Multi-sites', tone: 'accent' },
            'independent': { label: 'Indépendant', tone: 'success' },
        };
        const badge = badges[ownerType] || { label: ownerType, tone: 'neutral' as const };
        return <Badge tone={badge.tone}>{badge.label}</Badge>;
    };

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-6xl px-4 md:px-8 py-8">
                {/* Back Button */}
                <button
                    onClick={() => navigate("/restaurants")}
                    className="flex items-center gap-2 text-ink-muted hover:text-ink mb-6 transition-colors duration-150"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-sm font-medium">Retour à la base de données</span>
                </button>

                {/* Header Card */}
                <div className="bg-surface border border-border rounded-2xl p-6 mb-6">
                    <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                            <div className="w-16 h-16 bg-ink rounded-lg flex items-center justify-center">
                                <Store className="w-8 h-8 text-ink-inverse" />
                            </div>
                            <div>
                                <h1 className="font-sans text-3xl font-semibold text-ink mb-2">{restaurant.name}</h1>
                                <div className="flex flex-wrap items-center gap-2">
                                    {getOwnerTypeBadge(restaurant.ownerType)}
                                    <Badge tone="neutral">{restaurant.type}</Badge>
                                    <Badge tone="neutral">{restaurant.category}</Badge>
                                </div>
                            </div>
                        </div>

                        {restaurant.averageRating && (
                            <div className="flex items-center gap-2 bg-warning-subtle px-4 py-2 rounded-lg">
                                <Star className="w-5 h-5 text-warning fill-current" />
                                <span className="text-lg font-semibold text-ink">{restaurant.averageRating.toFixed(1)}</span>
                                {restaurant.numberOfReviews && (
                                    <span className="text-sm text-ink-muted">({restaurant.numberOfReviews} avis)</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Main Info */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Location Info */}
                        <div className="bg-surface border border-border rounded-2xl p-6">
                            <h2 className="text-lg font-semibold text-ink mb-4 flex items-center gap-2">
                                <MapPin className="w-5 h-5 text-ink-muted" />
                                Localisation
                            </h2>
                            <div className="space-y-3">
                                <div>
                                    <div className="text-sm font-medium text-ink-muted mb-1">Adresse</div>
                                    <div className="text-ink">{restaurant.address}</div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="text-sm font-medium text-ink-muted mb-1">Ville</div>
                                        <div className="text-ink">{restaurant.city}</div>
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium text-ink-muted mb-1">Région</div>
                                        <div className="text-ink">{restaurant.region}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Franchise Info (if applicable) */}
                        {(restaurant.franchise || restaurant.ownerName) && (
                            <div className="bg-surface border border-border rounded-2xl p-6">
                                <h2 className="text-lg font-semibold text-ink mb-4 flex items-center gap-2">
                                    <Store className="w-5 h-5 text-ink-muted" />
                                    Propriétaire
                                </h2>
                                <div className="space-y-3">
                                    {restaurant.franchise && (
                                        <div>
                                            <div className="text-sm font-medium text-ink-muted mb-1">Franchise</div>
                                            <div className="text-ink font-medium">{restaurant.franchise}</div>
                                        </div>
                                    )}
                                    {restaurant.ownerName && (
                                        <div>
                                            <div className="text-sm font-medium text-ink-muted mb-1">Nom du propriétaire</div>
                                            <div className="text-ink">{restaurant.ownerName}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Specialty */}
                        {restaurant.specialty && (
                            <div className="bg-surface border border-border rounded-2xl p-6">
                                <h2 className="text-lg font-semibold text-ink mb-4 flex items-center gap-2">
                                    <Sparkles className="w-5 h-5 text-ink-muted" />
                                    Spécialité
                                </h2>
                                <p className="text-ink-muted">{restaurant.specialty}</p>
                            </div>
                        )}
                    </div>

                    {/* Right Column - Stats & Contact */}
                    <div className="space-y-6">
                        {/* Contact Info */}
                        <div className="bg-surface border border-border rounded-2xl p-6">
                            <h2 className="text-lg font-semibold text-ink mb-4">Contact</h2>
                            <div className="space-y-3">
                                {restaurant.phone && (
                                    <div className="flex items-start gap-3">
                                        <Phone className="w-5 h-5 text-ink-muted mt-0.5" />
                                        <div>
                                            <div className="text-sm font-medium text-ink-muted mb-1">Téléphone</div>
                                            <a
                                                href={`tel:${restaurant.phone}`}
                                                className="text-ink hover:underline font-medium"
                                            >
                                                {restaurant.phone}
                                            </a>
                                        </div>
                                    </div>
                                )}
                                {restaurant.email && (
                                    <div className="flex items-start gap-3">
                                        <svg className="w-5 h-5 text-ink-muted mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <div>
                                            <div className="text-sm font-medium text-ink-muted mb-1">Email</div>
                                            <a
                                                href={`mailto:${restaurant.email}`}
                                                className="text-ink hover:underline font-medium break-all"
                                            >
                                                {restaurant.email}
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Restaurant Stats */}
                        <div className="bg-surface border border-border rounded-2xl p-6">
                            <h2 className="text-lg font-semibold text-ink mb-4">Statistiques</h2>
                            <div className="space-y-4">
                                {restaurant.seats && (
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-ink-muted">
                                            <Users className="w-5 h-5" />
                                            <span className="text-sm">Capacité</span>
                                        </div>
                                        <span className="font-semibold text-ink">{restaurant.seats} places</span>
                                    </div>
                                )}

                                {restaurant.yearOpened && (
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-ink-muted">
                                            <Calendar className="w-5 h-5" />
                                            <span className="text-sm">Ouverture</span>
                                        </div>
                                        <span className="font-semibold text-ink">{restaurant.yearOpened}</span>
                                    </div>
                                )}

                                {restaurant.averageRating && (
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-ink-muted">
                                            <Star className="w-5 h-5" />
                                            <span className="text-sm">Note moyenne</span>
                                        </div>
                                        <span className="font-semibold text-ink">{restaurant.averageRating.toFixed(1)}/5</span>
                                    </div>
                                )}

                                {restaurant.numberOfReviews && (
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-ink-muted">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                            </svg>
                                            <span className="text-sm">Avis</span>
                                        </div>
                                        <span className="font-semibold text-ink">{restaurant.numberOfReviews}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
